import type { ClientImageDto } from '~/store/images'

/**
 * Human byte size. Accepts the API's string-encoded BigInts.
 *
 * A genuine ZERO renders as `0 B`, never as a dash. The dash means "we do not
 * know" — an upload still in flight has no size yet — and using it for a real
 * zero would make an empty allowance read as an unmeasured one.
 */
export function formatImageBytes(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—'
  const bytes = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let n = bytes
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i += 1
  }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${units[i]}`
}

/** Transfer rate, for an in-flight upload. */
export function formatRate(bytesPerSecond: number | null): string {
  if (!bytesPerSecond || bytesPerSecond <= 0) return '—'
  return `${formatImageBytes(bytesPerSecond)}/s`
}

/** Coarse duration. Precision past a minute is noise on a multi-GB upload. */
export function formatEta(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))}s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}min`
}

/**
 * Whole days until an artifact is purged.
 *
 * Null when nothing is scheduled — which must render as silence rather than as
 * a reassuring "no expiry", since the only rows without one are broken.
 */
export function daysUntilExpiry(image: ClientImageDto): number | null {
  if (!image.expires_at) return null
  const ms = new Date(image.expires_at).getTime() - Date.now()
  if (!Number.isFinite(ms)) return null
  return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)))
}

/** An icon that says what KIND of artifact this is at a glance. */
export function imageIcon(image: ClientImageDto): string {
  return image.kind === 'iso' ? 'i-lucide-disc-3' : 'i-lucide-hard-drive'
}
